import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageBubble } from './MessageBubble';
import { FormattedContent } from './FormattedContent';
import { LinkPreviewCard } from './LinkPreviewCard';
import { MessageMeta } from './MessageMeta';
import { CharacterCount } from './CharacterCount';
import { MediaPreview } from './MediaPreview';
import type { MediaPreviewItem } from '../../stores/preview-store';
import type { PreviewState } from '../../types';
import './preview-panel.css';

/** Extract the first URL from preview state (entities then raw text) */
function extractFirstUrl(state: PreviewState | null): string | null {
  if (!state) return null;
  // Check text_link entities first (explicit links with href)
  for (const e of state.entities) {
    if (e.type === 'text_link' && 'url' in e) {
      return e.url;
    }
  }
  // Fall back to raw URL detection in text
  const match = /https?:\/\/\S+/i.exec(state.text);
  return match ? match[0] : null;
}

interface TelegramPreviewProps {
  /** HTML content from exportToTelegramHTML() */
  html?: string;
  /** Character count for the content */
  charCount: number;
  /** Character limit (4096 for messages, 1024 for captions) */
  limit: number;
  /** Whether the content exceeds the limit */
  isOverLimit: boolean;
  /** Media items to render above the text */
  mediaPreview?: MediaPreviewItem[];
  /** Content mode: 'message' (text only) or 'caption' (has media) */
  mode?: 'message' | 'caption';
  /** Preview state for link extraction */
  previewState?: PreviewState | null;
  /** Whether link preview is enabled */
  linkPreviewEnabled?: boolean;
  /** Render the built-in character counter below the bubble. Default true. */
  showCharCount?: boolean;
}

/**
 * TelegramPreview component
 *
 * Renders content styled to match the Telegram message bubble appearance.
 * Shows a warning banner when content exceeds the character limit.
 * Supports media (photos/videos) above the caption text.
 */
export function TelegramPreview({
  html,
  charCount,
  limit,
  isOverLimit,
  mediaPreview,
  mode = 'message',
  previewState,
  linkPreviewEnabled,
  showCharCount = true,
}: TelegramPreviewProps) {
  const { t } = useTranslation('preview');
  const { t: tc } = useTranslation('common');
  const hasMedia = mediaPreview && mediaPreview.length > 0;
  const hasContent = html && charCount > 0;

  // Use mode (set synchronously) instead of hasMedia (loads async) to avoid flash
  const firstUrl = useMemo(
    () => (linkPreviewEnabled && mode === 'message' ? extractFirstUrl(previewState ?? null) : null),
    [previewState, linkPreviewEnabled, mode]
  );

  if (!hasMedia && !hasContent) {
    return (
      <div className="tg-preview-bg flex min-h-full flex-col">
        <div className="tg-preview-empty flex-1">
          {t('startTyping')}
        </div>
        {showCharCount && (
          <div className="mt-auto pt-4">
            <CharacterCount count={0} limit={limit} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="tg-preview-bg flex min-h-full flex-col">
      {isOverLimit && (
        <div className="tg-limit-warning">
          {t('exceedsLimit', { mode: hasMedia ? tc('caption') : tc('message') })}
        </div>
      )}

      <MessageBubble className={hasMedia ? 'p-0 overflow-hidden' : undefined}>
        {hasMedia && <MediaPreview items={mediaPreview} />}
        <div className={hasMedia ? 'p-3' : undefined}>
          {hasContent && <FormattedContent html={html} />}
          {firstUrl && <LinkPreviewCard url={firstUrl} />}
          <MessageMeta />
        </div>
      </MessageBubble>

      {showCharCount && (
        <div className="mt-4">
          <CharacterCount count={charCount} limit={limit} />
        </div>
      )}
    </div>
  );
}
